import React, { useState, useEffect, useMemo, useRef } from 'react';
import { GalleryItem } from '../../types/gallery';
import GridImage from './GridImage';
import { getOptimizedUrl as defaultGetOptimizedUrl } from '../../utils/utils';
import { saveMainPageScroll, restoreMainPageScroll } from '../../utils/scrollRestoration';
import './GalleryGrid.css';

export interface GalleryGridProps {
  items: GalleryItem[];
  getOptimizedUrl?: (path: string, width?: number) => string;
  onRowCountCalculated?: (rowCount: number) => void;
  className?: string;
}

interface TooltipState {
  visible: boolean;
  text: string;
  x: number;
  y: number;
}

export const GalleryGrid: React.FC<GalleryGridProps> = ({
  items,
  getOptimizedUrl = defaultGetOptimizedUrl,
  onRowCountCalculated,
  className = ''
}) => {
  const [tooltip, setTooltip] = useState<TooltipState>({
    visible: false,
    text: '',
    x: 0,
    y: 0
  });

  const containerRef = useRef<HTMLDivElement | null>(null);

  // Chunk items into rows of 3 GridImages per row
  const rows = useMemo(() => {
    const chunked: GalleryItem[][] = [];
    for (let i = 0; i < items.length; i += 3) {
      chunked.push(items.slice(i, i + 3));
    }
    return chunked;
  }, [items]);

  useEffect(() => {
    if (rows.length > 0) {
      onRowCountCalculated?.(rows.length);
      if (typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent('schz-gallery-loaded', { detail: { rowCount: rows.length } })
        );
      }
    }
  }, [rows.length, onRowCountCalculated]);

  // Restore and preserve scroll depth on exhibition grid
  useEffect(() => {
    restoreMainPageScroll();

    const handleScroll = () => {
      saveMainPageScroll();
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('beforeunload', saveMainPageScroll);

    return () => {
      saveMainPageScroll();
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('beforeunload', saveMainPageScroll);
    };
  }, []);

  const handleMouseEnter = (item: GalleryItem) => {
    let title = item.id.replace(/-qm/g, '?').replace(/-c/g, ':');
    if (title.includes('untitled')) title = '[untitled]';
    setTooltip((prev) => ({
      ...prev,
      visible: true,
      text: `${title} (${item.year})`
    }));
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLImageElement>) => {
    setTooltip((prev) => ({
      ...prev,
      x: e.clientX,
      y: e.clientY
    }));
  };

  const handleMouseLeave = () => {
    setTooltip((prev) => ({ ...prev, visible: false }));
  };

  return (
    <main className={`main ${className}`} ref={containerRef}>
      {rows.map((row, rIdx) => (
        <div key={`row-${rIdx}`} className="image-row">
          {row.map((item) => (
            <GridImage
              key={item.id}
              item={item}
              color={item.color}
              getOptimizedUrl={getOptimizedUrl}
              onMouseEnter={handleMouseEnter}
              onMouseMove={handleMouseMove}
              onMouseLeave={handleMouseLeave}
            />
          ))}
        </div>
      ))}

      {/* Difference blend-mode hover tooltip */}
      <div
        className="custom-tooltip"
        style={{
          position: 'fixed',
          left: `${tooltip.x}px`,
          top: `${tooltip.y}px`,
          opacity: tooltip.visible ? 1 : 0,
          pointerEvents: 'none',
          zIndex: 99999
        }}
      >
        <span>{tooltip.text}</span>
      </div>
    </main>
  );
};

export default GalleryGrid;
